import { useEffect, useRef } from "react";

import { createAxiosInstance } from "api/axiosConfig";

const HEARTBEAT_INTERVAL_MS = 60_000;

// Keeps the signed-in staff member "online" by pinging
// POST /{tenant}/staff/presence/heartbeat on an interval. Pauses on
// hidden tabs and fires immediately when the tab returns to visible
// so a backgrounded tab doesn't hold the user online forever.
export const useStaffPresenceHeartbeat = ({ enabled = true } = {}) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const timerRef = useRef(null);

  useEffect(() => {
    if (!tenantVal || !enabled) return undefined;

    const fire = async () => {
      try {
        const axiosInstance = await createAxiosInstance();
        await axiosInstance.post(`${tenantVal}/staffmember/presence/heartbeat`);
      } catch {
        // Fail-soft: the next tick will retry.
      }
    };

    const startInterval = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(fire, HEARTBEAT_INTERVAL_MS);
    };

    const stopInterval = () => {
      if (!timerRef.current) return;
      clearInterval(timerRef.current);
      timerRef.current = null;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        fire();
        startInterval();
      }
    };

    fire();
    startInterval();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopInterval();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [tenantVal, enabled]);
};
